import * as ChannelService from '@channel.io/channel-web-sdk-loader'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { useContext, useEffect, useState } from 'react'
import type { ChangeEvent } from 'react'
import { isAxiosError } from 'axios'
import styled from 'styled-components'

import { MODALS_ID, QUERY_KEY } from '../../constants'

import { abrCompleteWithdrawalAccountRegister, abrStartWithdrawalAccount } from '../../utils/abr'

import { createWithdrawalAccount, updateWithdrawalAccount } from '../../api/withdrawalAccount'

import { StoresContext } from '../../provider/StoreProvider'
import { BottomSheetContext } from '../../provider/BottomSheetProvider'
import { ModalContext } from '../../provider/ModalProvider'

import { useAlert } from '../../hooks'
import { useNavigate } from '../../hooks/useNavigate'
import { useCheckPrevPathname } from '../../hooks/useCheckPrevPathname'
import { useUserInfo } from '../../hooks/useUserInfo'

import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { CenterWrap } from '../../components/common'
import { BankIcon } from '../../components/bankIcon'
import { Button } from '../../components/button'
import { Input } from '../../components/input'
import { SecurityIcon } from '../../components/securityIcon'
import { Text } from '../../components/text'
import type { BankName } from '../../components/bankIcon/BankIcon'
import type { SecurityName } from '../../components/securityIcon/SecurityIcon'
import { Footer } from '../../components/footer'
import { Header } from '../../components/header'
import { Spinner } from '../../components/spinner/Spinner'
import { WithdrawalAccountBottomSheet } from './bottomSheet'
import { SuccessCreatedWithdrawalModal } from './modals/SuccessCreatedWithdrawalModal'

import { Color } from '../../theme/color'

import { ReactComponent as ArrowDownIcon } from '../../assets/ico/common/expand/expand_down.svg'

// TODO: 지워야함
interface APIResponse {
  bank: { code: string; name: BankName }[]
  securities: { code: string; name: SecurityName }[]
}

// *출금 계좌 선택 페이지*
// 출금 계좌 선택하는 페이지(은행 및 증권사 선택)
// 로그인, 본인인증 유무 상관 없이 [출금 계좌 등록 시작 페이지]에서 온 게 아니라면 무조건 접근 불가
export function SelectWithdrawalBank() {
  const {
    commonStore: { setShowTab, setInvalidAccess },
  } = useContext(StoresContext)
  const { showBottomSheet } = useContext(BottomSheetContext)
  const { openModal } = useContext(ModalContext)

  const [withdrawalAccount, setWithdrawalAccount] = useState<
    ((APIResponse['bank'][0] | APIResponse['securities'][0]) & { type: 'bank' | 'securities' }) | undefined
  >()
  const [account, setAccount] = useState<string>('')

  const queryClient = useQueryClient()
  const { data: userInfo, isLoading, isError } = useUserInfo()
  const isValidPrevPathname = useCheckPrevPathname(['/account/withdrawal/create', '/withdrawalAccount'])
  const navigate = useNavigate()
  const openAlert = useAlert()

  const isUpdate = !!userInfo?.withdrawalAccount

  const { mutate } = useMutation({
    mutationFn: isUpdate ? updateWithdrawalAccount : createWithdrawalAccount,
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.withdrawalAccount] })

      // 출금 계좌 변경
      if (!isUpdate && response) {
        abrCompleteWithdrawalAccountRegister({ bankName: withdrawalAccount?.name })
        ChannelService.track('complete_withdrawal_account_register')
        openModal({ id: MODALS_ID.SUCCESS_CREATED_WITHDRAWAL, Component: SuccessCreatedWithdrawalModal })
        return
      }

      // 출금 계좌 새롭게 등록
      navigate(`/account/withdrawal/success?isUpdate=${isUpdate}`, {
        state: { prevPathname: window.location.pathname },
      })
    },
    onError: (error) => {
      if (isAxiosError(error)) {
        openAlert({
          content: (
            <Text size={16} weight={500} $textAlign="center">
              {error?.response?.data?.message || '은행 또는 계좌번호를 확인해 주세요.'}
            </Text>
          ),
        })
      }
    },
  })

  useEffect(() => {
    if (sessionStorage.getItem('isLastAccountCreatePage') === 'true') {
      sessionStorage.removeItem('isLastAccountCreatePage')
      navigate(-1)
      return
    }
    abrStartWithdrawalAccount()
    ChannelService.track('start_withdrawal_account_register')
    setShowTab(false)
  }, [])

  useEffect(() => {
    setInvalidAccess(!isValidPrevPathname)
  }, [isValidPrevPathname])

  if (isLoading || isError) {
    return (
      <Wrap>
        <Header type="SUB" title="출금 계좌" />
        <CenterWrap>
          <Spinner />
        </CenterWrap>
      </Wrap>
    )
  }

  const openWithdrawalAccountBottomSheet = () => {
    showBottomSheet({
      Component: () => <WithdrawalAccountBottomSheet onSelect={(info) => setWithdrawalAccount(info)} />,
    })
  }

  const handleAccount = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    if (Number(value) < 0) return
    setAccount(value)
  }

  const registAccount = async () => {
    if (!userInfo || (!userInfo.name && !userInfo.identification?.danalName)) {
      openAlert({
        content: (
          <Text size={16} weight={500} $textAlign="center">
            유저 정보가 올바르지 않습니다.
          </Text>
        ),
      })
      return
    }

    if (!withdrawalAccount || !account) {
      openAlert({
        content: (
          <Text size={16} weight={500} $textAlign="center">
            은행 또는 계좌번호를 확인해 주세요.
          </Text>
        ),
      })
      return
    }

    const phoneRegex = /^(010)?[0-9]{4}?[0-9]{4}$/
    if (phoneRegex.test(account)) {
      openAlert({
        content: (
          <Text size={16} weight={500} $textAlign="center">
            본 계좌 번호가 아닌 평생 계좌 번호는
            <br />
            등록이 불가능해요.
          </Text>
        ),
      })
      return
    }

    const { code } = withdrawalAccount
    const requestWithdrawalAccountData = {
      account,
      bankCode: code,
      bankOwner: userInfo.identification?.danalName || userInfo.name || '',
      // bankOwner: userInfo.name || userInfo.identification?.danalName || '',
    }

    mutate(requestWithdrawalAccountData)
  }

  return (
    <Wrap>
      <Header type="SUB" title="출금 계좌" />
      <Content>
        <Container>
          <Text size={24} weight={600} lineHeight={34} block={true}>
            고객님 명의의 출금계좌
            <br />
            등록이 필요해요.
          </Text>
          {withdrawalAccount ? (
            <>
              <AccountContainer onClick={openWithdrawalAccountBottomSheet}>
                <AccountInfoBox>
                  {withdrawalAccount.type === 'bank' && (
                    <BankIcon size={46} name={withdrawalAccount.name as BankName} />
                  )}
                  {withdrawalAccount.type === 'securities' && (
                    <SecurityIcon size={46} name={withdrawalAccount.name as SecurityName} />
                  )}
                  <AccountInfo>
                    <Text size={18} weight={600}>
                      {withdrawalAccount.name}
                    </Text>
                    <Text size={14} weight={600} color="text_secondary">
                      예금주 명: {userInfo?.identification?.danalName || userInfo?.name}
                      {/* 예금주 명: {userInfo?.name || userInfo?.identification?.danalName} */}
                    </Text>
                  </AccountInfo>
                </AccountInfoBox>
                <ArrowDownIcon />
              </AccountContainer>
              <Input.Line
                type="number"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="계좌번호를 입력해주세요."
                value={account}
                onChange={handleAccount}
                onRemove={() => setAccount('')}
              />
            </>
          ) : (
            <AddWithdrawalBox onClick={openWithdrawalAccountBottomSheet}>
              <Text size={16} weight={700} color="primary_5">
                본인 명의의 출금 계좌를 등록해주세요.
              </Text>
              <ArrowDownIcon />
            </AddWithdrawalBox>
          )}
        </Container>
        <Footer>
          <Button
            type="primary"
            size="large"
            disabled={!withdrawalAccount || !account.trim()}
            block={true}
            onClick={registAccount}
          >
            등록하기
          </Button>
        </Footer>
      </Content>
    </Wrap>
  )
}

const Container = styled.div`
  padding: 24px;
`

const AddWithdrawalBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 32px;
  padding: 16px;
  border-radius: 8px;
  background-color: ${Color.primary_2};
`

const AccountContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin: 32px 0 40px;
  padding: 16px;
  border-radius: 8px;
  background-color: ${Color.gray_1};
`

const AccountInfoBox = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const AccountInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`
